/**
 * Hooks React Query pour l'authoring du DAG « solve-backward ».
 *
 * - `useTypesDeProbleme` : catalogue des types (sélecteur). Vide tant que les
 *   seeds 0146/0147 ne sont pas chargés.
 * - `useBuildDag` : appelle l'edge fn `dag-build` (LLM + validation catalogue +
 *   persistance via RPC `save_dag`).
 * - `useDag` : lit le DAG d'un exercice via la RPC `get_dag`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { BuildDagInput, BuildDagResult, DagResult, TypeDeProbleme } from './types';

// Le catalogue (types_de_probleme…) et les RPC DAG (get_dag) sont POSTÉRIEURS à
// types.generated.ts (stale volontaire, cf. CLAUDE.md). On les requête via un
// handle non-typé + cast `as unknown as` du résultat (même pattern que le blog).
const db = supabase as unknown as SupabaseClient;

export class DagBuildError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'DagBuildError';
  }
}

export const dagQueryKey = (exerciseId: string) => ['dag', exerciseId] as const;

/** Invoque l'edge fn `dag-build` et remonte le message d'erreur métier du body. */
async function invokeBuild(body: BuildDagInput): Promise<BuildDagResult> {
  const { data, error } = await supabase.functions.invoke<BuildDagResult>('dag-build', { body });
  if (error) {
    let message = error.message;
    let code: string | undefined;
    const ctx = (error as Error & { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as { error?: string; message?: string };
        code = parsed.error;
        message = parsed.message ?? parsed.error ?? message;
      } catch {
        /* garde le message générique */
      }
    }
    throw new DagBuildError(message, code);
  }
  if (!data) throw new DagBuildError('empty_response');
  return data;
}

export function useTypesDeProbleme() {
  return useQuery({
    queryKey: ['dag-types-de-probleme'],
    queryFn: async (): Promise<TypeDeProbleme[]> => {
      // Jointure objets_math → niveau_apparition de l'objet du type (filtrage par niveau).
      const { data, error } = await db
        .from('types_de_probleme')
        .select('id, geste_id, objet_id, description, objets_math(niveau_apparition)')
        .order('id');
      if (error) throw new Error(error.message);
      return (
        (data ?? []) as unknown as Array<{
          id: string;
          geste_id: string;
          objet_id: string;
          description: string;
          objets_math: { niveau_apparition: string } | { niveau_apparition: string }[] | null;
        }>
      ).map((row) => {
        const om = Array.isArray(row.objets_math) ? row.objets_math[0] : row.objets_math;
        return {
          id: row.id,
          geste_id: row.geste_id,
          objet_id: row.objet_id,
          description: row.description,
          niveau_apparition: om?.niveau_apparition ?? '6e',
        };
      });
    },
    staleTime: 5 * 60_000,
  });
}

export function useBuildDag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: invokeBuild,
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: dagQueryKey(vars.exercise_id) });
    },
  });
}

export function useDag(exerciseId: string | null | undefined) {
  return useQuery({
    queryKey: dagQueryKey(exerciseId ?? ''),
    enabled: !!exerciseId,
    queryFn: async (): Promise<DagResult> => {
      const { data, error } = await db.rpc('get_dag', { p_exercise_id: exerciseId });
      if (error) throw new Error(error.message);
      return data as unknown as DagResult;
    },
  });
}
